/**
 * Agent Resolve — a pure, deterministic port of the prototype's `resolveCore`.
 *
 * Solves for the working year: make held fit the ceiling with the smallest intervention.
 * Levers the user has already set are respected and never moved; only untouched levers adjust,
 * in order of preference — free releases first (dead stock, then the smallest pack-family
 * count, then certified fitting sets, then a deeper relocate threshold up to the safe stop of
 * 11), then the commercial ask (pacing, in small steps), then capital last (build handover §9).
 *
 * No network, no LLM — this is what both the "Agent Resolve" button AND the chat's "close the
 * gap" intent call, so the two always agree.
 */

import {
  runScenario,
  type Scenario5LeverId,
  type ScenarioContext,
  type ScenarioParams,
} from "@/lib/data";

import { CONV_CERTIFIED_MAX, SYSTEMS } from "./mock";

/**
 * Capital is the last resort, and when we reach for it we pick the cheapest system per face —
 * the same rule the backend resolver uses (`capacity_resolve._cheapest_system`), so the button
 * and the chat's resolve agree even if the economics table is edited later.
 */
const CHEAPEST_SYSTEM = SYSTEMS.reduce((best, s) =>
  s.addCostM / s.addFacesFraction < best.addCostM / best.addFacesFraction ? s : best,
).key;

export interface ResolveOutcome {
  params: ScenarioParams;
  note: string;
  tone: "ok" | "warn";
}

const LEVER_NAME: Record<Scenario5LeverId, string> = {
  relo: "relocate",
  pack: "pack",
  conv: "convert",
  cap: "capacity",
  pace: "NPI pacing",
};

const fmt = (n: number) => Math.round(n).toLocaleString("en-US");

/** The relocate threshold is never pushed past this by the resolver — deeper than the C band
 *  starts touching stock people actively order, and the guidance is to escalate rather than
 *  force it (build handover §4/§9). */
const RELO_SAFE_STOP = 11;

export function resolveScenario(before: ScenarioParams, ctx: ScenarioContext): ResolveOutcome {
  const touched: Record<Scenario5LeverId, boolean> = {
    relo: before.reloThreshold >= 0,
    pack: before.packCount > 0,
    conv: before.convCount > 0,
    cap: !!before.capSystem && before.capUnits > 0,
    pace: before.pacePct > 0,
  };
  const keptList = (Object.keys(touched) as Scenario5LeverId[]).filter((k) => touched[k]);
  const roomOf = (p: ScenarioParams) => runScenario(p, ctx).room;

  if (roomOf(before) >= 0) {
    return {
      params: before,
      note: `Nothing to resolve. The current settings already meet the ${before.year} requirement with <b>+${fmt(roomOf(before))}</b> slots of room.`,
      tone: "ok",
    };
  }

  let draft: ScenarioParams = { ...before };
  const changed: Scenario5LeverId[] = [];

  // 1. Free release: dead stock (threshold 0), no cost, no customer impact.
  if (!touched.relo) {
    draft = { ...draft, reloThreshold: 0 };
    changed.push("relo");
  }
  // 2. Smallest pack-family count that closes the gap.
  if (roomOf(draft) < 0 && !touched.pack) {
    let v = 1;
    while (v < 10 && roomOf({ ...draft, packCount: v }) < 0) {
      v++;
    }
    draft = { ...draft, packCount: v };
    changed.push("pack");
  }
  // 3. Certified fitting sets (≤ the 4th — never reaches for an uncertified set).
  if (roomOf(draft) < 0 && !touched.conv) {
    let v = 1;
    while (v < CONV_CERTIFIED_MAX && roomOf({ ...draft, convCount: v }) < 0) {
      v++;
    }
    draft = { ...draft, convCount: v };
    changed.push("conv");
  }
  // 4. Deeper relocate threshold, up to the safe stop.
  if (roomOf(draft) < 0 && !touched.relo) {
    let v = draft.reloThreshold;
    while (v < RELO_SAFE_STOP && roomOf({ ...draft, reloThreshold: v }) < 0) {
      v++;
    }
    draft = { ...draft, reloThreshold: v };
  }
  // 5. Commercial ask, in small steps.
  if (roomOf(draft) < 0 && !touched.pace) {
    let v = 5;
    while (v < 100 && roomOf({ ...draft, pacePct: v }) < 0) {
      v += 5;
    }
    draft = { ...draft, pacePct: v };
    changed.push("pace");
  }
  // 6. Capital last.
  if (roomOf(draft) < 0 && !touched.cap) {
    let v = 1;
    while (v < 6 && roomOf({ ...draft, capSystem: CHEAPEST_SYSTEM, capUnits: v }) < 0) {
      v++;
    }
    draft = { ...draft, capSystem: CHEAPEST_SYSTEM, capUnits: v };
    changed.push("cap");
  }

  draft = { ...draft, agentSet: changed };
  const room = roomOf(draft);

  const did: string[] = [];
  if (changed.includes("relo")) {
    did.push(
      draft.reloThreshold === 0
        ? "relocate to dead stock only"
        : `relocate to ≤ ${draft.reloThreshold} picks/yr`,
    );
  }
  if (changed.includes("pack")) {
    did.push(`${draft.packCount} of 10 pack families`);
  }
  if (changed.includes("conv")) {
    did.push(`${draft.convCount} of 8 fitting sets`);
  }
  if (changed.includes("pace")) {
    did.push(`${draft.pacePct}% of the intake deferred`);
  }
  if (changed.includes("cap")) {
    did.push(`${draft.capUnits} unit${draft.capUnits === 1 ? "" : "s"} of Pick-to-Light`);
  }
  const kept = keptList.length
    ? ` Your ${keptList.map((k) => LEVER_NAME[k]).join(", ")} setting${keptList.length > 1 ? "s were" : " was"} kept as set.`
    : "";

  if (room >= 0) {
    return {
      params: draft,
      note: `Resolved: ${did.join(" · ")}.${kept} Room in ${draft.year}: <b>+${fmt(room)}</b>.`,
      tone: "ok",
    };
  }
  return {
    params: draft,
    note: `Adjusted every free lever (${did.join(" · ")}) but still <b>${fmt(-room)}</b> short.${kept} Freeing a fixed lever would give the agent more to work with.`,
    tone: "warn",
  };
}
